// src/services/admin/ArtistMerchServices.js
import { useState, useCallback } from "react";
import API from "../../api/apiAdmin";
import { useNotification } from "../../context/NotificationContext";

export const useArtistMerch = () => {
    const [loading, setLoading] = useState(false);
    const { showNotification } = useNotification();

    const fetchAllMerch = useCallback(async () => {
        setLoading(true);
        try {
            const response = await API.get("/admin/inventory/merch/");
            return response.data;
        } catch (error) {
            console.error("Failed to fetch artist merch:", error);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchMerchProductById = useCallback(async (id) => {
        setLoading(true);
        try {
            const response = await API.get(`/admin/inventory/merch/${id}`);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch merch details:", error);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchMerchCategories = useCallback(async () => {
        try {
            const response = await API.get("/admin/inventory/merch/categories");
            return response.data;
        } catch (error) {
            console.error("Failed to fetch merch categories:", error);
            return [];
        }
    }, []);

    const createMerchProduct = async (productData) => {
        setLoading(true);
        try {
            const formData = new FormData();
            Object.keys(productData).forEach(key => {
                if (key === 'image_file' && productData[key]) {
                    formData.append('image', productData[key]);
                } else if (productData[key] !== null && productData[key] !== undefined) {
                    formData.append(key, productData[key]);
                }
            });

            const response = await API.post("/admin/inventory/merch/", formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (response.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Product Created",
                    message: response.data.message
                });
            }
            return response.data;
        } catch (error) {
            const msg = error.response?.data?.message || "Failed to create merch product.";
            showNotification({
                type: "failed",
                title: "Error",
                message: msg
            });
            return error.response?.data || { status: "failed", message: msg };
        } finally {
            setLoading(false);
        }
    };

    const updateMerchProduct = async (id, productData) => {
        setLoading(true);
        try {
            const formData = new FormData();
            Object.keys(productData).forEach(key => {
                if (key === 'image_file' && productData[key]) {
                    formData.append('image', productData[key]);
                } else if (productData[key] !== null && productData[key] !== undefined) {
                    formData.append(key, productData[key]);
                }
            });

            const response = await API.put(`/admin/inventory/merch/${id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (response.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Product Updated",
                    message: response.data.message
                });
            }
            return response.data;
        } catch (error) {
            const msg = error.response?.data?.message || "Failed to update merch product.";
            showNotification({
                type: "failed",
                title: "Error",
                message: msg
            });
            return error.response?.data || { status: "failed", message: msg };
        } finally {
            setLoading(false);
        }
    };

    const deleteMerchProduct = async (id) => {
        setLoading(true);
        try {
            const response = await API.delete(`/admin/inventory/merch/${id}`);
            if (response.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Product Deleted",
                    message: response.data.message
                });
            }
            return response.data;
        } catch (error) {
            const msg = error.response?.data?.message || "Failed to delete merch product.";
            showNotification({
                type: "failed",
                title: "Error",
                message: msg
            });
            return error.response?.data || { status: "failed", message: msg };
        } finally {
            setLoading(false);
        }
    };

    const toggleMerchStatus = async (id) => {
        try {
            const response = await API.post(`/admin/inventory/merch/${id}/toggle-status`);
            if (response.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Status Updated",
                    message: response.data.message
                });
            }
            return response.data;
        } catch (error) {
            const msg = error.response?.data?.message || "Failed to toggle status.";
            showNotification({
                type: "failed",
                title: "Error",
                message: msg
            });
            return error.response?.data || { status: "failed", message: msg };
        }
    };

    return {
        loading,
        fetchAllMerch,
        fetchMerchProductById,
        fetchMerchCategories,
        createMerchProduct,
        updateMerchProduct,
        deleteMerchProduct,
        toggleMerchStatus
    };
};
